import { getErrorMessage } from '../../utils/serviceUtils';
import { randomId } from '../../utils/randomId';
import { useEffect, useRef, useState, useCallback } from 'react';
import { GameState, GameEvent } from '../../types/game';
import { eventStore } from '../../services/EventStore';
import { sendOnlineCommand } from '../../services/transport/onlineTransportMirror';
import { recordDebugTrace } from '../debug/debugLog';

interface UseScorePersistenceDeps {
  gameState: GameState;
  refreshSnapshot?: (reason: string) => Promise<boolean>;
}

export interface ScoreSyncStatus {
  pendingScoreCount: number;
}

type SequencedEvent = GameEvent & { sequenceNumber?: number };

const hasServerSequence = (event: GameEvent): boolean => {
  return typeof (event as SequencedEvent).sequenceNumber === 'number';
};

const isScoreLikeEvent = (event: GameEvent): event is Extract<GameEvent, { type: 'SCORE' | 'PENALTY' }> => {
  return event.type === 'SCORE' || event.type === 'PENALTY';
};

const isOvertakeEvent = (event: GameEvent): event is Extract<GameEvent, { type: 'OVERTAKE' }> => {
  return event.type === 'OVERTAKE';
};

const isString = (value: unknown): value is string => typeof value === 'string';

const isDuplicateKeyError = (result: Awaited<ReturnType<typeof eventStore.appendEvent>>): boolean => {
  return Boolean(result.error?.includes('duplicate key'));
};

const toFiniteNumber = (value: unknown, fallback = 0): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const isPersistedEvent = (event: GameEvent, persistedIds: Set<string>): boolean => {
  return hasServerSequence(event) || persistedIds.has(event.id);
};

const applyScoreImpact = (scores: Map<string, number>, event: GameEvent, direction: 1 | -1 = 1): void => {
  if (isScoreLikeEvent(event)) {
    const playerId = event.payload.playerId;
    if (!playerId) return;
    const delta = toFiniteNumber(event.payload.delta, 0) * direction;
    const prev = scores.get(playerId) ?? 0;
    scores.set(playerId, prev + delta);
    return;
  }

  if (isOvertakeEvent(event)) {
    const overtakerId = event.payload.overtakerId;
    if (overtakerId) {
      const bonus = toFiniteNumber(event.payload.bonus, 0) * direction;
      const prev = scores.get(overtakerId) ?? 0;
      scores.set(overtakerId, prev + bonus);
    }
    const penalties = Array.isArray(event.payload.penalties) ? event.payload.penalties : [];
    penalties.forEach((penalty) => {
      const victimId = penalty.playerId;
      if (!victimId) return;
      const delta = toFiniteNumber(penalty.delta, 0) * direction;
      const prev = scores.get(victimId) ?? 0;
      scores.set(victimId, prev + delta);
    });
  }
};

const buildPersistedScoresBaseline = (
  players: GameState['players'],
  events: GameEvent[],
  persistedIds: Set<string>,
): Map<string, number> => {
  // Use current scoreboard as source of truth and subtract local, non-confirmed deltas.
  // This makes SCORE validation resilient to trimmed event history.
  const baseline = new Map<string, number>();
  players.forEach((player) => {
    baseline.set(player.id, toFiniteNumber(player.score, 0));
  });

  events.forEach((event) => {
    if (isPersistedEvent(event, persistedIds)) return;
    applyScoreImpact(baseline, event, -1);
  });

  return baseline;
};

// Phase 5: Persists SCORE / PENALTY / OVERTAKE events into remote eventStore.
// Idempotent: lokalny Set przechowuje już zapisane event ids.
export function useScorePersistence({ gameState, refreshSnapshot }: UseScorePersistenceDeps) {
  const persistedIds = useRef<Set<string>>(new Set());
  const ensuredRealModeByGame = useRef<Set<string>>(new Set());
  const currentGameId = useRef<string | null>(null);
  const seenOvertakes = useRef<Set<string>>(new Set());
  const recoveryRequestedByEvent = useRef<Set<string>>(new Set());
  const recoveryInFlight = useRef(false);
  const queueRef = useRef<Promise<void>>(Promise.resolve());
  const [pendingScoreCount, setPendingScoreCount] = useState(0);

  const syncPendingScores = useCallback((events: GameEvent[]) => {
    if (gameState.mode === 'ONLINE') {
      setPendingScoreCount((prev) => (prev === 0 ? prev : 0));
      return;
    }
    const pending = events.filter((ev) => (
      (ev.type === 'SCORE' || ev.type === 'PENALTY')
      && Boolean(ev.id)
      && !persistedIds.current.has(ev.id)
    ));
    setPendingScoreCount((prev) => (prev === pending.length ? prev : pending.length));
  }, [gameState.mode]);

  // Hydrate persisted IDs per game from localStorage
  useEffect(() => {
    if (!gameState.id) return;
    if (currentGameId.current === gameState.id) return;
    currentGameId.current = gameState.id;
    queueRef.current = Promise.resolve(); // reset kolejki przy zmianie gry
    queueMicrotask(() => setPendingScoreCount(0));
    ensuredRealModeByGame.current = new Set();
    recoveryRequestedByEvent.current = new Set();
    recoveryInFlight.current = false;
    try {
      seenOvertakes.current = new Set();
      const raw = localStorage.getItem(`dice1k_persisted_events_${gameState.id}`);
      if (raw) {
        const arr = JSON.parse(raw);
        if (Array.isArray(arr)) {
          persistedIds.current = new Set(arr.filter(isString));
        }
      }
    } catch {
      persistedIds.current = new Set();
    }
  }, [gameState.id]);

  const persistCache = useCallback(() => {
    if (!gameState.id) return;
    try {
      localStorage.setItem(
        `dice1k_persisted_events_${gameState.id}`,
        JSON.stringify(Array.from(persistedIds.current)),
      );
    } catch {}
  }, [gameState.id]);

  const requestSnapshotRecovery = useCallback(async (
    gameId: string,
    event: Extract<GameEvent, { type: 'SCORE' | 'PENALTY' }>,
    reason: 'local-mismatch' | 'rpc-reject',
    details: Record<string, unknown>,
  ) => {
    if (!refreshSnapshot) return;
    if (recoveryRequestedByEvent.current.has(event.id)) return;
    if (recoveryInFlight.current) return;
    recoveryRequestedByEvent.current.add(event.id);
    recoveryInFlight.current = true;
    recordDebugTrace(
      'SCORE_PERSIST_RECOVERY_REQUESTED',
      {
        gameId,
        eventId: event.id,
        eventType: event.type,
        turn: event.turn,
        reason,
        ...details,
      },
      { source: 'useScorePersistence', tags: ['recovery', 'score-sync'] },
      { force: true }
    );
    try {
      const refreshed = await refreshSnapshot(`score-persist-${reason}`);
      recordDebugTrace(
        'SCORE_PERSIST_RECOVERY_RESULT',
        {
          gameId,
          eventId: event.id,
          eventType: event.type,
          turn: event.turn,
          reason,
          refreshed,
        },
        { source: 'useScorePersistence', tags: ['recovery', 'score-sync'] },
        { force: true }
      );
    } catch (error) {
      recordDebugTrace(
        'SCORE_PERSIST_RECOVERY_FAILED',
        {
          gameId,
          eventId: event.id,
          eventType: event.type,
          turn: event.turn,
          reason,
          error: getErrorMessage(error),
        },
        { source: 'useScorePersistence', tags: ['recovery', 'score-sync'] },
        { force: true }
      );
    } finally {
      recoveryInFlight.current = false;
    }
  }, [refreshSnapshot]);

  const ensureRealMode = useCallback(async (gameId: string): Promise<boolean> => {
    if (ensuredRealModeByGame.current.has(gameId)) return true;
    const ok = await sendOnlineCommand(gameId, {
      type: 'GAME_CREATED',
      eventId: randomId(),
      mode: 'REAL',
    });
    if (ok) {
      ensuredRealModeByGame.current.add(gameId);
      return true;
    }
    recordDebugTrace(
      'REAL_MODE_ENSURE_FAILED',
      { gameId },
      { source: 'useScorePersistence', tags: ['score-sync', 'real-mode'] },
      { force: true }
    );
    return false;
  }, []);

  useEffect(() => {
    if (!gameState.id) return;
    const gameId = gameState.id;
    const events: GameEvent[] = gameState.events || [];
    const persistedScores = buildPersistedScoresBaseline(gameState.players, events, persistedIds.current);
    const runtimeScores = new Map(persistedScores);
    // zbuduj mapę już zapisanych overtake (po turn + overtakerId) z sekwencją
    seenOvertakes.current = new Set(
      events
        .filter((ev): ev is Extract<GameEvent, { type: 'OVERTAKE' }> => (
          isOvertakeEvent(ev) && isPersistedEvent(ev, persistedIds.current)
        ))
        .map((ev) => `${ev.turn}:${ev.payload.overtakerId || ''}`),
    );

    queueMicrotask(() => syncPendingScores(events));
    queueRef.current = queueRef.current.then(async () => {
      for (const ev of events) {
        if (!ev?.id) continue;
        if (isPersistedEvent(ev, persistedIds.current)) {
          persistedIds.current.add(ev.id);
          if (isOvertakeEvent(ev)) {
            const key = `${ev.turn}:${ev.payload.overtakerId || ''}`;
            seenOvertakes.current.add(key);
          }
          continue; // już w bazie
        }

        if (isOvertakeEvent(ev)) {
          const key = `${ev.turn}:${ev.payload.overtakerId || ''}`;
          if (seenOvertakes.current.has(key) || persistedIds.current.has(ev.id)) {
            continue;
          }
          if (gameState.mode !== 'REAL') {
            continue;
          }
          const modeReady = await ensureRealMode(gameId);
          if (!modeReady) {
            continue;
          }
          let overtakeRes: Awaited<ReturnType<typeof eventStore.appendEvent>>;
          try {
            overtakeRes = await eventStore.appendEvent(gameId, ev);
          } catch (err) {
            overtakeRes = { success: false, error: err instanceof Error ? err.message : String(err) };
          }
          if (overtakeRes.success || isDuplicateKeyError(overtakeRes)) {
            persistedIds.current.add(ev.id);
            seenOvertakes.current.add(key);
            applyScoreImpact(runtimeScores, ev);
            persistCache();
            syncPendingScores(events);
          }
          continue;
        }

        if (isScoreLikeEvent(ev) && !persistedIds.current.has(ev.id)) {
          // W ONLINE 0 pkt są rozliczane przez serwer intentów; w REAL 0 pkt muszą być replikowane (zmiana tury).
          const delta = Number(ev.payload.delta ?? 0);
          if (ev.type === 'SCORE' && delta === 0 && gameState.mode !== 'REAL') {
            persistedIds.current.add(ev.id);
            syncPendingScores(events);
            continue;
          }
          const isReal = gameState.mode === 'REAL';
          if (isReal) {
            const modeReady = await ensureRealMode(gameId);
            if (!modeReady) {
              continue;
            }
          }
          if (!isReal) {
            // Spójność z ostatnim potwierdzonym wynikiem
            const pid = ev.payload.playerId;
            const prevPersisted = pid ? (runtimeScores.get(pid) ?? 0) : 0;
            const resulting = Number(ev.payload.resultingScore ?? prevPersisted + delta);
            const expected = prevPersisted + delta;
            if (resulting !== expected) {
              recordDebugTrace(
                'SCORE_PERSIST_LOCAL_MISMATCH',
                {
                  gameId,
                  eventId: ev.id,
                  turn: ev.turn,
                  playerId: pid || null,
                  eventType: ev.type,
                  delta,
                  resultingScore: resulting,
                  expectedResultingScore: expected,
                  prevPersistedScore: prevPersisted,
                },
                { source: 'useScorePersistence', tags: ['local-mismatch', 'score-sync'] },
                { force: true }
              );
              await requestSnapshotRecovery(gameId, ev, 'local-mismatch', {
                playerId: pid || null,
                delta,
                resultingScore: resulting,
                expectedResultingScore: expected,
                prevPersistedScore: prevPersisted,
              });
              // nie wysyłamy zdarzenia, które na pewno zwróci SCORE_MISMATCH
              continue;
            }
          }
          const eventToSend: GameEvent = ev;
          let res: Awaited<ReturnType<typeof eventStore.appendEvent>>;
          try {
            res = await eventStore.appendEvent(gameId, eventToSend);
          } catch (err) {
            res = { success: false, error: err instanceof Error ? err.message : String(err) };
          }
          if (res.success || isDuplicateKeyError(res)) {
            persistedIds.current.add(ev.id);
            applyScoreImpact(runtimeScores, ev);
            persistCache();
            syncPendingScores(events);
          } else if (ev.type === 'SCORE' || ev.type === 'PENALTY') {
            const playerId = ev.payload.playerId || null;
            const player = playerId ? gameState.players.find(p => p.id === playerId) : null;
            const delta = Number(ev.payload.delta ?? 0);
            const resultingScore = ev.payload.resultingScore ?? null;
            const relatedOvertakes = events
              .filter((candidate): candidate is Extract<GameEvent, { type: 'OVERTAKE' }> => (
                isOvertakeEvent(candidate) && candidate.turn === ev.turn
              ))
              .slice(-3)
              .map((candidate) => ({
                id: candidate.id,
                overtakerId: candidate.payload.overtakerId || null,
                bonus: candidate.payload.bonus ?? null,
                penalties: candidate.payload.penalties ?? null,
              }));
            const recentScores = events
              .filter((candidate): candidate is Extract<GameEvent, { type: 'SCORE' | 'PENALTY' }> => isScoreLikeEvent(candidate))
              .slice(-5)
              .map((candidate) => ({
                id: candidate.id,
                type: candidate.type,
                turn: candidate.turn,
                playerId: candidate.payload.playerId || null,
                delta: candidate.payload.delta ?? null,
                resultingScore: candidate.payload.resultingScore ?? null,
              }));

            recordDebugTrace(
              'SCORE_PERSIST_REJECTED',
              {
                gameId,
                eventId: ev.id,
                eventType: ev.type,
                turn: ev.turn,
                playerId,
                playerName: player?.name || null,
                delta,
                resultingScore,
                localScore: player?.score ?? null,
                pendingScoreCount,
                payload: eventToSend.payload,
                error: res?.error || null,
                errorCode: res?.errorCode || null,
                errorDetails: res?.errorDetails || null,
                errorHint: res?.errorHint || null,
                relatedOvertakes,
                recentScores,
                localScores: gameState.players.map(p => ({
                  id: p.id,
                  name: p.name,
                  score: p.score,
                  hasFirstEntry: p.hasFirstEntry,
                })),
              },
              { source: 'useScorePersistence', tags: ['rpc-reject', 'score-sync'] },
              { force: true, flushReason: 'score-reject' }
            );
            const rawError = `${res?.error || ''} ${res?.errorCode || ''}`.toUpperCase();
            const recoverableReject = (
              rawError.includes('SCORE_MISMATCH')
              || rawError.includes('ACTIVE_PLAYER_MISMATCH')
              || rawError.includes('OVERTAKE_MISMATCH')
              || rawError.includes('OVERTAKE_BONUS_MISMATCH')
            );
            if (recoverableReject) {
              await requestSnapshotRecovery(gameId, ev, 'rpc-reject', {
                error: res?.error || null,
                errorCode: res?.errorCode || null,
              });
            }
          }
        }
      }
    });
  }, [
    gameState.events,
    gameState.id,
    gameState.mode,
    gameState.players,
    pendingScoreCount,
    persistCache,
    requestSnapshotRecovery,
    ensureRealMode,
    syncPendingScores,
  ]);

  return { pendingScoreCount };
}

